import * as React from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import PopupState, {
  bindPopover,
  bindTrigger,
} from "material-ui-popup-state";

import FacebookIcon from "@mui/icons-material/Facebook";
import XIcon from "@mui/icons-material/X";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useTheme } from "@mui/material/styles";


export default function SharePopover({
  shareUrl,
  shareTitle = "",
  shareText = "",
}) {
  const [copied, setCopied] = React.useState(false);

  const theme = useTheme();

  const encodedUrl = encodeURIComponent(shareUrl || "");
  const encodedTitle = encodeURIComponent(shareTitle || "");
  const encodedText = encodeURIComponent(shareText || "");

  const xText = encodeURIComponent(
    shareText || shareTitle || ""
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Nie udało się skopiować linku:", error);
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) return;

    try {
      await navigator.share({
        title: shareTitle,
        text: shareText,
        url: shareUrl,
      });
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Błąd udostępniania:", error);
      }
    }
  };

  const socialButtons = [
    {
      name: "Facebook",
      icon: <FacebookIcon />,
      color: "#1877F2",
      hoverColor: "#166FE5",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      name: "X",
      icon: <XIcon />,
      color: "#000000",
      hoverColor: "#333333",
      url: `https://x.com/intent/post?text=${xText}&url=${encodedUrl}`,
    },
    {
      name: "LinkedIn",
      icon: <LinkedInIcon />,
      color: "#0A66C2",
      hoverColor: "#004182",
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
  ];

  return (
    <PopupState
      variant="popover"
      popupId="share-popover"
    >
      {(popupState) => (
        <>
          <Button
            {...bindTrigger(popupState)}
            startIcon={<ShareIcon />}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Udostępnij
          </Button>

          <Popover
            {...bindPopover(popupState)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  width: 320,
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                },
              },
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ mb: 0.5 }}
              >
                Udostępnij
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Udostępnij tę stronę w wybranym serwisie.
              </Typography>

              <Stack spacing={1}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "center",
                  }}
                >
                  {socialButtons.map((button) => (
                    <IconButton
                      key={button.name}
                      component="a"
                      href={button.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Udostępnij na ${button.name}`}
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "50%",
                        color: button.color,
                        backgroundColor: `${button.color}12`,
                        border: "1px solid",
                        borderColor: `${button.color}25`,
                        transition: "all 0.2s ease",

                        "&:hover": {
                          color: "#fff",
                          backgroundColor: button.color,
                          borderColor: button.color,
                          transform: "translateY(-2px)",
                          boxShadow: `0 5px 14px ${button.color}35`,
                        },

                        "&:active": {
                          transform: "translateY(0)",
                        },

                        "& svg": {
                          fontSize: 20,
                        },
                      }}
                    >
                      {button.icon}
                    </IconButton>
                  ))}
                </Stack>

                {typeof navigator !== "undefined" &&
                  navigator.share && (
                    <>
                      <Divider sx={{ my: 0.5 }} />

                      <Button
                        onClick={handleNativeShare}
                        fullWidth
                        startIcon={<ShareIcon />}
                        sx={{
                          justifyContent: "flex-start",
                          px: 1.5,
                          py: 1,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                        }}
                      >
                        Więcej opcji...
                      </Button>
                    </>
                  )}

                <Button
                  onClick={handleCopy}
                  fullWidth
                  startIcon={
                    copied ? <CheckIcon /> : <ContentCopyIcon />
                  }
                  sx={{
                    justifyContent: "flex-start",
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    color: copied
                      ? "success.main"
                      : "text.primary",
                  }}
                >
                  {copied ? "Skopiowano link" : "Kopiuj link"}
                </Button>
              </Stack>
            </Box>
          </Popover>
        </>
      )}
    </PopupState>
  );
}


// import * as React from "react";
// import Typography from "@mui/material/Typography";
// import Button from "@mui/material/Button";
// import Popover from "@mui/material/Popover";
// import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state";
// import { ButtonGroup } from "@mui/material";
// import FacebookIcon from "@mui/icons-material/Facebook";
// import TwitterIcon from "@mui/icons-material/Twitter";
// import LinkedInIcon from "@mui/icons-material/LinkedIn";
// import ShareIcon from "@mui/icons-material/Share";

// export default function PopoverPopupState({ shareUrl }) {
//   const buttons = [
//     <a
//       key="facebook"
//       href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
//       style={{ textDecoration: "none" }}
//       target="_blank"
//     >
//       <Button
//         startIcon={<FacebookIcon />}
//         sx={{ width: "100%", borderRadius: 0, color: '#4267B2' }}
//         key="facebook"
//       >
//         Facebook
//       </Button>
//     </a>,
//     <a
//       key="tweeter"
//       href={`https://twitter.com/intent/tweet?url=${shareUrl}`}
//       style={{ textDecoration: "none" }}
//       target="_blank"
//     >
//       <Button
//         startIcon={<TwitterIcon />}
//         sx={{ width: "100%", borderRadius: 0, color: '#1DA1F2' }}
//         key="twitter"
//       >
//         Twitter
//       </Button>
//     </a>,
//     <a
//       key="linkedin"
//       href={`https://www.linkedin.com/cws/share?url=${shareUrl}`}
//       style={{ textDecoration: "none" }}
//       target="_blank"
//     >
//       <Button
//         startIcon={<LinkedInIcon />}
//         sx={{ width: "100%", borderRadius: 0, color: '#0e76a8' }}
//         key="linkedin"
//       >
//         LinkedIn
//       </Button>
//     </a>,
//   ];

//   return (
//     <PopupState variant="popover" popupId="demo-popup-popover">
//       {(popupState) => (
//         <div>
//           <Button
//             {...bindTrigger(popupState)}
//             startIcon={<ShareIcon />}
//             size="small"
//           >
//             Udostępnij
//           </Button>
//           <Popover
//             {...bindPopover(popupState)}
//             anchorOrigin={{
//               vertical: "bottom",
//               horizontal: "center",
//             }}
//             transformOrigin={{
//               vertical: "top",
//               horizontal: "center",
//             }}
//           >
//             <ButtonGroup
//               orientation="vertical"
//               aria-label="vertical contained button group"
//               size="small"
//             >
//               {buttons}
//             </ButtonGroup>
//           </Popover>
//         </div>
//       )}
//     </PopupState>
//   );
// }
